import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Gift, Package, Truck, Check, X, Clock, ArrowLeft, ExternalLink, Search } from "lucide-react";
import { useAdmin } from "./AdminPages";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_LABELS = {
  pending: { label: "À traiter", color: "amber", icon: Clock },
  in_progress: { label: "En cours", color: "blue", icon: Package },
  shipped: { label: "Expédiée", color: "emerald", icon: Truck },
  delivered: { label: "Livrée", color: "slate", icon: Check },
  cancelled: { label: "Annulée", color: "red", icon: X },
};

const STATUS_FILTERS = ["all", "pending", "in_progress", "shipped", "delivered", "cancelled"];

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_LABELS[status] || { label: status, color: "slate", icon: Clock };
  const Icon = cfg.icon;
  const colorMap = {
    amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700",
    emerald: "bg-emerald-100 text-emerald-700",
    slate: "bg-slate-100 text-slate-600",
    red: "bg-red-100 text-red-700",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${colorMap[cfg.color]}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

export const AdminBusinessCardsPage = () => {
  const { admin, token } = useAdmin();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // request being edited

  useEffect(() => {
    if (!admin) { navigate("/admin/login"); return; }
    void fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const url = filter === "all"
        ? `${API_URL}/admin/business-cards/requests`
        : `${API_URL}/admin/business-cards/requests?status=${filter}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setRequests(res.data || []);
    } catch (e) {
      // ignore — empty list shown
    }
    setLoading(false);
  };

  const updateStatus = async (id, payload) => {
    try {
      await axios.patch(
        `${API_URL}/admin/business-cards/requests/${id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchRequests();
      setEditing(null);
    } catch (e) {
      alert("Erreur lors de la mise à jour");
    }
  };

  const filtered = requests.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const hay = `${r.practitioner_name} ${r.practitioner_email} ${r.shipping?.city || ""} ${r.id}`.toLowerCase();
    return hay.includes(q);
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="p-2 rounded-lg hover:bg-slate-100" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Gift className="w-5 h-5 text-emerald-600" />
                Cartes de visite — Demandes
              </h1>
              <p className="text-sm text-slate-500">Concierge "100 cartes offertes"</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher (nom, email, ville…)"
                className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none w-64"
                data-testid="bizcard-search"
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Status filter pills */}
        <div className="flex gap-2 mb-6 flex-wrap" data-testid="status-filters">
          {STATUS_FILTERS.map((s) => {
            const isActive = filter === s;
            return (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-emerald-600 text-white"
                    : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
                data-testid={`filter-${s}`}
              >
                {s === "all" ? "Toutes" : (STATUS_LABELS[s]?.label || s)}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
            <Gift className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-semibold">Aucune demande pour ce filtre.</p>
            <p className="text-sm text-slate-400 mt-1">Les nouvelles demandes apparaîtront ici.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map((r) => (
              <RequestCard
                key={r.id}
                request={r}
                onEdit={() => setEditing(r)}
                apiBase={process.env.REACT_APP_BACKEND_URL}
              />
            ))}
          </div>
        )}
      </main>

      {editing && (
        <EditRequestModal
          request={editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => updateStatus(editing.id, payload)}
        />
      )}
    </div>
  );
};

const RequestCard = ({ request, onEdit, apiBase }) => {
  const apiOrigin = (apiBase || "").replace(/\/$/, "");
  const pdfUrl = `${apiOrigin}/api/doctors/${request.practitioner_id}/business-card.pdf`;
  const ship = request.shipping || {};

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`request-card-${request.id}`}>
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h3 className="text-base font-bold text-slate-900">{request.practitioner_name}</h3>
            <StatusBadge status={request.status} />
          </div>
          <p className="text-sm text-slate-500">
            {request.practitioner_specialty || "—"} · {request.practitioner_email}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Soumise le {formatDate(request.created_at)} · {request.quantity || 100} cartes
          </p>
        </div>
        <div className="flex gap-2">
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-semibold flex items-center gap-1.5"
            data-testid={`download-pdf-${request.id}`}
          >
            <ExternalLink className="w-4 h-4" /> PDF
          </a>
          <button
            onClick={onEdit}
            className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold"
            data-testid={`edit-status-${request.id}`}
          >
            Modifier
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <div className="bg-slate-50 rounded-xl p-3">
          <p className="text-xs uppercase font-bold text-slate-500 mb-1">Adresse de livraison</p>
          <p className="text-slate-700 leading-relaxed">
            {ship.full_name}<br />
            {ship.line1}<br />
            {ship.line2 && <>{ship.line2}<br /></>}
            {ship.postal_code} {ship.city}<br />
            {ship.country || "France"}<br />
            <span className="text-slate-500">Tél : {ship.phone}</span>
          </p>
        </div>
        <div className="bg-slate-50 rounded-xl p-3 text-slate-700">
          <p className="text-xs uppercase font-bold text-slate-500 mb-1">Suivi</p>
          {request.tracking_number ? (
            <p>{request.carrier || "Transporteur"} — <span className="font-mono">{request.tracking_number}</span></p>
          ) : (
            <p className="text-slate-400 italic">Pas encore de suivi</p>
          )}
          {request.notes && (
            <>
              <p className="text-xs uppercase font-bold text-slate-500 mt-3 mb-1">Note praticien</p>
              <p className="text-slate-700 italic">"{request.notes}"</p>
            </>
          )}
          {request.admin_note && (
            <>
              <p className="text-xs uppercase font-bold text-slate-500 mt-3 mb-1">Note admin</p>
              <p className="text-slate-700">{request.admin_note}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EditRequestModal = ({ request, onClose, onSave }) => {
  const [status, setStatus] = useState(request.status || "pending");
  const [tracking, setTracking] = useState(request.tracking_number || "");
  const [carrier, setCarrier] = useState(request.carrier || "");
  const [adminNote, setAdminNote] = useState(request.admin_note || "");

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { status };
    if (tracking) payload.tracking_number = tracking;
    if (carrier) payload.carrier = carrier;
    if (adminNote) payload.admin_note = adminNote;
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="edit-request-modal">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Mettre à jour la commande</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs uppercase font-bold text-slate-500 mb-1 block">Statut</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
              data-testid="status-select"
            >
              {Object.keys(STATUS_LABELS).map((k) => (
                <option key={k} value={k}>{STATUS_LABELS[k].label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase font-bold text-slate-500 mb-1 block">Transporteur</label>
              <input
                type="text"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                placeholder="Colissimo, Chronopost…"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                data-testid="carrier-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-slate-500 mb-1 block">N° de suivi</label>
              <input
                type="text"
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
                placeholder="AB123456789FR"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                data-testid="tracking-input"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase font-bold text-slate-500 mb-1 block">Note interne</label>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
              data-testid="admin-note-input"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold"
              data-testid="cancel-edit"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
              data-testid="save-edit"
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminBusinessCardsPage;
